/**
 * Chart tokens.
 *
 * The categorical slots are used in fixed order and never cycled — a series
 * keeps its colour regardless of its rank, so filtering never repaints the
 * survivors. This four-slot set was validated against the app's white card
 * surface: all four clear the lightness band, the chroma floor, and CVD
 * separation (worst adjacent pair ΔE 9.1). Aqua and yellow fall below 3:1
 * contrast, so anything drawn in them carries a visible label — never colour
 * alone.
 */
export const chartColors = {
  /** Categorical slots, in assignment order. */
  series: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'] as const,

  /** The single hue used wherever one series carries the whole chart. */
  primary: '#2a78d6',

  /**
   * Ordered steps for a ranked sequence — funnel stages, tiers, bands — where
   * the categories have a real order and the ramp encodes position in it, not
   * magnitude (bar length already carries that).
   *
   * Drawn from the product green so a funnel reads as part of the brand rather
   * than as a foreign chart colour. Validated light→dark: monotone lightness,
   * every adjacent ΔL ≥ 0.06, single hue (4° spread), light end clear of the
   * surface. Never use these as categorical slots — they share one hue.
   *
   * Five steps, so a five-rung scale can use the whole ramp and a four-rung one
   * takes the first four. Both were validated; a lighter opening step fails the
   * light-end contrast floor against this surface.
   */
  ordinal: ['#82b8a7', '#5a9b86', '#43836d', '#2f6553', '#274f43'] as const,

  grid: '#e1e0d9',
  axis: '#c3c2b7',
  muted: '#898781',
  textPrimary: '#0b0b0b',
  textSecondary: '#52514e',
  surface: '#ffffff',
} as const;

/** Recharts axis styling — recessive, so the data carries the emphasis. */
export const axisProps = {
  tick: { fill: chartColors.muted, fontSize: 11 },
  tickLine: false,
  axisLine: false,
} as const;

/** `2026-08` → `Aug`, or `Aug 26` when a year boundary is in view. */
export function formatMonth(month: string, withYear = false): string {
  const [year, monthPart] = month.split('-');
  const index = Number(monthPart) - 1;

  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const name = names[index] ?? month;

  return withYear && year ? `${name} ${year.slice(2)}` : name;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}
